// Topbar del wizard (mock `wizard/*.html` §wizard-topbar): "Salir de la venta" + marca + identidad
// del vendedor. Esa identidad NO es decorativa: el wizard es pantalla completa sin el AppShell del
// portal, y sin ella la pantalla se lee como "me deslogueó y me pide entrar de nuevo" cuando en
// realidad la sesión es la MISMA (misma SPA, misma cookie). Mostrar quién vende y desde qué tienda
// es lo que hace evidente que las credenciales se comparten.
import { Link } from '@tanstack/react-router';
import { ArrowLeft, ShieldCheck, XCircle } from 'lucide-react';
import {
    ACCOUNT_OPENING_BRAND_SUFFIX,
    ACCOUNT_OPENING_CANCEL,
    ACCOUNT_OPENING_EXIT,
} from '../accountOpening';
import { useSellerContext } from '../sellerContext';
import { useWizardExit } from './WizardExit';

const EXIT_CLASS =
    'flex flex-none items-center gap-1 whitespace-nowrap text-[11px] text-muted-foreground transition-colors hover:text-foreground';

/**
 * Salir a media venta pide el motivo, igual que el «Guardar y salir» del pie: irse por la flecha
 * dejaba la venta abierta sin que nadie supiera por qué. Donde el paso no admite prospecto —la
 * captura de teléfono, el cierre— sigue siendo un enlace directo al portal.
 */
function ExitLink({ accountOpening }: { accountOpening: boolean }) {
    const exit = useWizardExit();
    const label = accountOpening ? ACCOUNT_OPENING_EXIT : 'Salir de la venta';

    // La apertura no se guarda como prospecto: su salida siempre es directa.
    if (accountOpening || !exit?.canSaveProspect) {
        return (
            <Link to="/sales" className={EXIT_CLASS}>
                <ArrowLeft className="size-3.5" aria-hidden /> {label}
            </Link>
        );
    }

    return (
        <button type="button" onClick={exit.openProspectDrawer} className={EXIT_CLASS}>
            <ArrowLeft className="size-3.5" aria-hidden /> {label}
        </button>
    );
}

/** «Cancelar venta» vive junto a la salida: es global a todos los pasos, no de un pie en particular. */
function CancelSaleButton({ accountOpening }: { accountOpening: boolean }) {
    const exit = useWizardExit();
    if (!exit?.canCancelSale || !exit.openCancelDialog) return null;
    return (
        <button
            type="button"
            onClick={exit.openCancelDialog}
            className="flex flex-none items-center gap-1 whitespace-nowrap text-[11px] text-destructive/80 transition-colors hover:text-destructive"
        >
            <XCircle className="size-3.5" aria-hidden />{' '}
            {accountOpening ? ACCOUNT_OPENING_CANCEL : 'Cancelar venta'}
        </button>
    );
}

export function WizardTopbar({
    children,
    accountOpening = false,
}: {
    children?: React.ReactNode;
    /** Apertura de cuenta sin venta: cambia el copy y quita la salida con prospecto. */
    accountOpening?: boolean;
}) {
    const { sellerName, storeId, hasStore } = useSellerContext();

    return (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2 sm:gap-x-3">
            <ExitLink accountOpening={accountOpening} />
            <CancelSaleButton accountOpening={accountOpening} />
            <span className="hidden flex-none text-muted-foreground sm:inline">/</span>
            <span className="flex size-7 flex-none items-center justify-center rounded-md bg-primary text-primary-foreground">
                <ShieldCheck className="size-4" strokeWidth={2.5} aria-hidden />
            </span>
            <span className="flex-none whitespace-nowrap text-[13px] font-semibold tracking-tight">
                SureKeep{' '}
                <span className="hidden font-normal text-muted-foreground sm:inline">
                    {accountOpening ? ACCOUNT_OPENING_BRAND_SUFFIX : '· Venta'}
                </span>
            </span>

            {/* El stepper baja a su propia fila hasta lg: en una sola fila aplasta la identidad del
                vendedor. min-w-0 para que scrollee en vez de empujar. */}
            <div className="order-last flex w-full min-w-0 items-center gap-3 lg:order-none lg:w-auto lg:flex-1 lg:justify-end">
                {children}
            </div>

            {/* Identidad del vendedor — misma sesión que el portal, sin volver a autenticarse.
                El storeId es un ULID largo: se trunca para no comerse el ancho del stepper. */}
            <div className="ml-auto flex-none border-l border-border pl-2 text-right leading-tight sm:pl-3">
                <div className="max-w-[110px] truncate text-[12px] font-medium sm:max-w-[160px]">
                    {sellerName || 'Sesión activa'}
                </div>
                <div
                    className="max-w-[110px] truncate text-[10px] text-muted-foreground sm:max-w-[160px]"
                    title={hasStore ? storeId : undefined}
                >
                    {hasStore ? `Vendedor · ${storeId}` : 'Sin tienda asignada'}
                </div>
            </div>
        </div>
    );
}
